import * as Sentry from '@sentry/angular';
import {
    type ApplicationConfig,
    provideBrowserGlobalErrorListeners,
    ErrorHandler,
    provideAppInitializer,
    inject,
    isDevMode,
    PLATFORM_ID,
} from '@angular/core';
import { provideRouter, Router, TitleStrategy } from '@angular/router';

import { routes } from './app.routes';
import {
    provideClientHydration,
    withEventReplay,
    withHttpTransferCacheOptions,
    withI18nSupport,
    withIncrementalHydration,
} from '@angular/platform-browser';
import { providePrimeNG } from 'primeng/config';
import { provideLucideConfig } from '@lucide/angular';
import { theme } from './app.theme';
import {
    HttpClient,
    HttpErrorResponse,
    HttpStatusCode,
    provideHttpClient,
    withFetch,
    withInterceptors,
} from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { cookiesStorage, provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { lastValueFrom } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@env/environment';
import { TranslocoTitleStrategy } from '@core/strategies/transloco-title.strategy';
import { AppErrorHandler } from '@core/handlers/app-error.handler';
import { credentialsInterceptor } from '@features/identity/interceptors/credentials.interceptor';
import { sessionExpiredInterceptor } from '@features/identity/interceptors/session-expired.interceptor';
import { AuthService } from '@features/identity/services/auth.service';
import { NavigationService } from '@features/navigation/services/navigation.service';
import { IDENTITY_KEYS } from '@features/identity/identity.query-keys';
import { NAVIGATION_KEYS } from '@features/navigation/navigation.query-keys';
import { MessageService } from 'primeng/api';
import { Location } from '@angular/common';

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        provideBrowserGlobalErrorListeners(),
        provideRouter(routes),
        // provideRouter(routes, withExperimentalPlatformNavigation(), withExperimentalAutoCleanupInjectors()),
        provideClientHydration(
            withEventReplay(),
            withIncrementalHydration(),
            withI18nSupport(),
            // Session/menu GETs must never replay the anonymous build-time prerender snapshot.
            withHttpTransferCacheOptions({
                filter: (req) => !req.url.includes('/auth/') && !req.url.includes('/navigation/menu'),
            }),
        ),
        {
            provide: ErrorHandler,
            useClass: AppErrorHandler,
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
        providePrimeNG({
            ripple: true,
            inputVariant: 'filled',
            theme: {
                preset: theme,
                options: {
                    darkModeSelector: '.app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'theme, base, primeng',
                    },
                },
            },
        }),
        provideLucideConfig({
            size: 16,
        }),
        provideHttpClient(withFetch(), withInterceptors([credentialsInterceptor, sessionExpiredInterceptor])),
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'es', label: 'Español' },
                    { id: 'en', label: 'English' },
                ],
                defaultLang: 'es',
                // Remove this option if your application doesn't support changing language in runtime.
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
                flatten: {
                    aot: !isDevMode(),
                },
            },
            loader: TranslocoHttpLoader, // Auto-generated via ng add
        }),
        provideTranslocoLocale({
            langToLocaleMapping: {
                es: 'es-MX',
                en: 'en-US',
            },
        }),
        provideTranslocoMessageformat(),
        provideTranslocoPersistLang({
            storage: {
                useFactory: () => cookiesStorage(),
            },
        }),
        provideTanStackQuery(
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: (failureCount, error) => {
                            // No point retrying an expired session / stale CSRF — bail so the
                            // sessionExpiredInterceptor redirect happens without a ~7s delay.
                            if (
                                error instanceof HttpErrorResponse &&
                                (error.status === HttpStatusCode.Unauthorized ||
                                    error.status === HttpStatusCode.Forbidden)
                            ) {
                                return false;
                            }
                            return failureCount < 3;
                        },
                    },
                },
            }),
            withDevtools(() => ({ loadDevtools: 'auto' })),
        ),
        provideAppInitializer(() => {
            const http = inject(HttpClient);
            const platformId = inject(PLATFORM_ID);
            const router = inject(Router);

            if (!isPlatformBrowser(platformId)) return;

            // A rejected initializer stalls bootstrap before Router mounts, leaving a blank screen.
            return lastValueFrom(http.get(Location.joinWithSlash(environment.apiUrl, 'auth/csrf'))).catch(() => {
                router.navigateByUrl('/error');
            });
        }),
        provideAppInitializer(() => {
            const platformId = inject(PLATFORM_ID);
            if (!isPlatformBrowser(platformId)) return;

            const queryClient = inject(QueryClient);
            const authService = inject(AuthService);
            const navigationService = inject(NavigationService);

            // Resolve the session (and menu) before any route guard runs, so a reload on a deep
            // route decides synchronously instead of racing a timeout and bouncing to the dashboard.
            return (async () => {
                const session = await queryClient
                    .fetchQuery({
                        queryKey: IDENTITY_KEYS.session(),
                        queryFn: () => lastValueFrom(authService.whoami()),
                        retry: false,
                        staleTime: 1000 * 60 * 5,
                    })
                    .catch(() => null);

                if (session) {
                    await queryClient
                        .prefetchQuery({
                            queryKey: NAVIGATION_KEYS.menu(),
                            queryFn: () => lastValueFrom(navigationService.getMenu()),
                            staleTime: Infinity,
                        })
                        .catch(() => undefined);
                }
            })();
        }),
        { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    ],
};
